import LandingPage from '~models/LandingPage';
import { getField, updateField } from 'vuex-map-fields';
import { Handler } from '~services';
import router from '~router';
import i18n from '../i18n';
import { landingPages } from '~api';

const SET_LANDING_PAGE = 'SET_LANDING_PAGE';
const RESET_LANDING_PAGE = 'RESET_LANDING_PAGE';
const LOADING = 'LOADING';

const state = {
    landingPage: { ...new LandingPage() },
    isLoading: false
};

const actions = {
    /**
     * Reset landing page state and redirect to landing page list
     * @param {function} commit
     */
    cancel({ commit }) {
        commit(RESET_LANDING_PAGE);
        router.push({ name: 'dashboard.pages' });
    },

    /**
     * Create manager account
     * @param {function} commit
     * @param {function} dispatch
     * @param {object} state
     */
    async create({ commit, state }) {
        const successMessage = i18n.t('managers.create.successMessage');

        commit(LOADING);

        const { title, description, sessionIds, themes } = state.landingPage;

        // Api request to create manager
        const resp = await landingPages.create({
            title,
            description,
            data: JSON.stringify({
                sessionIds,
                themes
            })
        });

        if (resp.ok) {
            commit(RESET_LANDING_PAGE);
        }

        // Handle response
        Handler.handleResponse(resp, successMessage, '', 'dashboard.pages');

        commit(LOADING);

        return resp;
    },

    /**
     * Get Manager account
     * @param {function} commit
     * @param  {string} id
     */
    async read({ commit }, id) {
        commit(LOADING);

        // Api request get manager account by id
        const resp = await landingPages.read(id);

        if (resp.ok) {
            const data = JSON.parse(resp.data.sessionsData);

            commit(SET_LANDING_PAGE, {
                ...resp.data,
                themes: data.themes,
                sessionIds: data.sessionIds
            });
        } else {
            // Handle response if error
            Handler.handleResponse(resp);
        }

        commit(LOADING);

        return resp;
    },

    /**
     * Update Manager account
     * @param {function} commit
     * @param params {object} params
     * @returns {Promise<any>}
     */
    async update({ commit, state }) {
        const successMessage = i18n.t('landingPages.edit.successMessage');

        commit(LOADING);

        const {
            id,
            title,
            description,
            sessionIds,
            themes
        } = state.landingPage;

        const resp = await landingPages.update(id, {
            title,
            description,
            data: JSON.stringify({
                sessionIds,
                themes
            })
        });

        Handler.handleResponse(resp, successMessage, '', 'dashboard.pages');

        // Reset store manager if account deleted
        if (resp.ok) {
            commit(RESET_LANDING_PAGE);
        }

        commit(LOADING);

        return resp;
    },

    /**
     * Delete Manager account.
     * @param {function} commit
     * @param {function} dispatch
     * @param {object} state
     * @param {string} id
     * @returns {Promise<void>}
     */
    async delete({ commit, dispatch }, id) {
        const successMessage = i18n.t('landingPages.delete.successMessage');

        // Show confirm modal window
        const confirmed = await dispatch(
            'prompt/show',
            i18n.t('prompt.deleteLandingPage'),
            { root: true }
        );
        if (!confirmed) return;

        commit('LOADING');

        // Api request on delete account
        const resp = await landingPages.delete(id);

        // Handle response
        Handler.handleResponse(resp, successMessage);

        commit(LOADING);

        return resp.ok;
    }
};

const mutations = {
    // Add the `updateField` mutation to the
    // `mutations` of your Vuex store instance.
    updateField,
    [SET_LANDING_PAGE](state, data) {
        state.landingPage = data;
    },
    [RESET_LANDING_PAGE](state) {
        state.landingPage = { ...new LandingPage() };
    },
    [LOADING](state) {
        state.isLoading = !state.isLoading;
    }
};

const getters = {
    // Add the `getField` getter to the
    // `getters` of your Vuex store instance.
    getField,
    isLoading: state => state.isLoading
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters
};
